import React, { useContext } from 'react'
import styled from 'styled-components'
import Carousel from '@jarvis.oddle/core/components/Carousel'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Button from '@jarvis.oddle/core/components/Button'
import PromotionCard from 'src/components/molecules/PromotionCard'
import IconArrow from '@jarvis.oddle/core/components/Icons/IconArrow'
import { H2 } from '@jarvis.oddle/core/components/Typo'
import { padding, paddingSizes } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { withResponsiveContext } from 'src/decorators/Responsive'
import { selectSections } from 'src/redux/customisation'
import useAppSelector from 'src/hooks/useAppSelector'
import ResponsiveSection from 'src/components/core/ResponsiveSection'
import modalController from 'src/decorators/ModalController'
import { selectAllPromotions } from 'src/redux/promotion'

const offsetPadding = 2

const HeadingTextContainer = styled(ResponsiveSection.Container)`
  text-align: ${(props) => props.textAlign};
`
HeadingTextContainer.defaultProps = {
  textAlign: 'center'
}

const PromotionBarContainer = styled(ResponsiveSection.Container)`
  padding: ${`${offsetPadding}px`} 0;
  max-width: 100vw;
`

const PromotionCardWrapper = styled.div`
  padding: ${offsetPadding}px ${padding[paddingSizes.extraSmall]};
  display: inline-block;
  vertical-align: bottom;
  width: 300px;
  ${breakpoint('tablet')`
    width: 33%;
  `}

  /* padding 2px is needed for the box-shadow of PromotionCard to be visible */
  ${breakpoint('desktop')`
    padding: ${offsetPadding}px ${padding[paddingSizes.small]};
  `}

  &:focus {
    outline: 0;
  }
`

const ArrowButton = styled(Button)`
  position: absolute;
  top: 50%;
  ${(props) => (props.left ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  height: 50px;
  width: 50px;
  padding: ${padding[paddingSizes.small]};
  z-index: 1;
`

const NextArrow = (props) => (
  <ArrowButton {...props} role='button'>
    {() => <IconArrow.Right fill='currentColor' iconSize='medium' />}
  </ArrowButton>
)

const PrevArrow = (props) => (
  <ArrowButton {...props} role='button' left>
    {() => <IconArrow.Left fill='currentColor' iconSize='medium' />}
  </ArrowButton>
)

const HorizontalScrollContainer = styled.div`
  padding-bottom: ${padding[paddingSizes.small]};
  text-align: center;
  overflow-x: auto;
  overflow-y: visible;
  white-space: nowrap;
`

interface PromotionFilmstripSection {
  id: string
  name: string
  settings: {
    showDefaultImage
    textAlignment: string
    headingsText: string
  }
  type: string
}

export interface PromotionFilmStripProps {
  id: string
  responsive: {
    tablet: boolean
    mobile: boolean
  }
}

const PromotionFilmStrip = ({
  responsive,
  id
}: PromotionFilmStripProps): JSX.Element => {
  const { settings } = useAppSelector(selectSections)[
    id
  ] as PromotionFilmstripSection
  const promotions: Promotion[] = useAppSelector(selectAllPromotions)?.data || []
  const { t } = useContext(TranslationContext)
  const getSlidesToShow = (promotions: Promotion[]) => {
    if (responsive.tablet || promotions.length < 3) {
      return 2
    } else {
      return 3
    }
  }

  const getPromoCards = ({
    promotions,
    showDefaultImage
  }: {
    promotions: Promotion[]
    showDefaultImage: boolean
  }) => {
    const cards = []

    if (promotions) {
      const promos = (Array.isArray(promotions) ? promotions : []).map(
        (p, i) => (
          <PromotionCardWrapper
            key={i}
            role='button'
            onClick={() =>
              modalController.showModal('PromotionDetailsModal', {
                promotion: p,
                showDefaultImage
              })
            }
          >
            <PromotionCard promotion={p} showDefaultImage={showDefaultImage} />
          </PromotionCardWrapper>
        )
      )
      cards.push(...promos)
    }

    return cards
  }

  const slidesToShow = getSlidesToShow(promotions)
  const showArrows = !responsive.mobile && promotions.length > slidesToShow
  // if the value is unset or true, we will show the banner image
  const showDefaultImage = settings.showDefaultImage !== false
  const promotionCards = getPromoCards({ promotions, showDefaultImage })
  const draggable = responsive.mobile && promotionCards.length > 1
  const framePadding = showArrows ? '0 50px' : 0

  return promotionCards.length > 0 ? (
    <ResponsiveSection>
      <HeadingTextContainer textAlign={settings.textAlignment}>
        <H2 sectionHeading>{t(settings.headingsText)}</H2>
      </HeadingTextContainer>
      <PromotionBarContainer>
        {responsive.mobile || promotionCards.length < 4 ? (
          <HorizontalScrollContainer>
            {promotionCards}
          </HorizontalScrollContainer>
        ) : (
          <Carousel
            infinite={false}
            lazyLoad={false}
            dots={false}
            arrows={showArrows}
            NextArrow={NextArrow}
            PrevArrow={PrevArrow}
            slidesToShow={slidesToShow}
            draggable={draggable}
            swipe={draggable}
            framePadding={framePadding}
            slidesToScroll={1}
          >
            {promotionCards}
          </Carousel>
        )}
      </PromotionBarContainer>
    </ResponsiveSection>
  ) : null
}

export default withResponsiveContext(PromotionFilmStrip)
